import { eventBus } from '../core/EventBus';
import { Game } from '../core/Game';
import { DetachedPanelState, SaveManager } from '../core/SaveManager';
import { CurrencyManager } from '../economy/CurrencyManager';
import { Plant } from '../plants/Plant';
import { ALL_SPECIES } from '../plants/PlantData';
import { iconGear, iconDownload, iconUpload, iconFlower, iconWaterDrop, iconSun, iconSprout, iconSunflower } from '../icons';
import { PlantManager } from '../managers/PlantManager';
import { StatusConsole } from './StatusConsole';

export class UIManager {
  private uiLayer: HTMLElement;
  private game: Game;
  private canvas: HTMLCanvasElement;
  private currencyManager: CurrencyManager;
  private isSettingsOpen: boolean = false;
  private isGardenView: boolean = false;
  private isSeedTrayOpen: boolean = false;
  private gardenDeletePending: string | null = null;

  private plants: () => Plant[];
  private plantManager: PlantManager | null = null;

  private plantSettingsPanel: HTMLDivElement | null = null;
  private hoveredPlant: Plant | null = null;
  private plantTagsContainer: HTMLDivElement | null = null;
  private controlTagExpanded: boolean = false;
  private hideTagsTimeout: ReturnType<typeof setTimeout> | null = null;
  private timeEl: HTMLElement | null = null;
  private countEl: HTMLElement | null = null;
  private statusConsole!: StatusConsole;

  constructor(uiLayer: HTMLElement, game: Game, canvas: HTMLCanvasElement, plantsFn: () => Plant[], currencyManager: CurrencyManager) {
    this.uiLayer = uiLayer;
    this.game = game;
    this.canvas = canvas;
    this.plants = plantsFn;
    this.currencyManager = currencyManager;
    this.createUI();
  }

  setPlantManager(pm: PlantManager): void {
    this.plantManager = pm;
  }

  private createUI(): void {
    const topBar = document.createElement('div');
    topBar.className = 'top-bar';

    const compact = document.createElement('div');
    compact.className = 'top-bar-compact';
    compact.innerHTML = `
      <span id="game-time" class="time-text">00:00</span>
      <span id="plant-count" class="count-text"></span>
      <span id="currency-display" class="currency-text"></span>
    `;
    topBar.appendChild(compact);
    this.uiLayer.appendChild(topBar);

    this.timeEl = compact.querySelector('#game-time');
    this.countEl = compact.querySelector('#plant-count');

    this.createSeedTrayButton();
    this.createSeedTrayPanel();

    const settingsBtn = document.createElement('button');
    settingsBtn.className = 'icon-btn settings-btn';
    settingsBtn.innerHTML = iconGear();
    settingsBtn.addEventListener('click', () => this.toggleSettings());
    this.uiLayer.appendChild(settingsBtn);

    this.createSettingsPanel();

    eventBus.on('game:update', () => this.updateDisplay());
    eventBus.on('currency:changed', () => this.updateCurrencyDisplay());
    this.updateCurrencyDisplay();

    compact.style.cursor = 'pointer';
    compact.title = '点击查看状态控制台';
    compact.addEventListener('click', () => this.toggleStatusConsole());

    this.statusConsole = new StatusConsole(topBar, this.plants, this.currencyManager, this.game);
  }

  private toggleStatusConsole(): void {
    const topBar = this.uiLayer.querySelector('.top-bar') as HTMLElement;
    if (!topBar) return;
    const willExpand = !topBar.classList.contains('expanded');
    topBar.classList.toggle('expanded');
    this.statusConsole.toggle(willExpand);
  }

  getDetachedConsolePanels(): DetachedPanelState[] {
    return this.statusConsole.getDetachedState();
  }

  restoreDetachedConsolePanels(panels: DetachedPanelState[]): void {
    this.statusConsole.restoreDetachedPanels(panels);
  }

  private createSeedTrayButton(): void {
    const btn = document.createElement('button');
    btn.id = 'seed-tray-btn';
    btn.className = 'icon-btn seed-btn';
    btn.innerHTML = iconSprout({size: 18});
    btn.title = '种子托盘 (P)';
    btn.addEventListener('click', () => this.toggleSeedTray());
    this.uiLayer.appendChild(btn);
  }

  private createSeedTrayPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'seed-tray-panel';
    panel.className = 'journal-panel seed-tray-panel';

    const title = document.createElement('h3');
    title.className = 'panel-title';
    title.textContent = '种子托盘';
    panel.appendChild(title);

    const cardsContainer = document.createElement('div');
    cardsContainer.id = 'seed-cards';
    cardsContainer.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

    for (const species of ALL_SPECIES) {
      const seedCard = document.createElement('div');
      seedCard.dataset.speciesId = species.id;
      seedCard.className = `seed-card ${species.id === 'sunflower' ? 'sunflower' : 'lavender'}`;

      const speciesIcon = species.id === 'sunflower'
        ? iconSunflower({ size: 22 })
        : iconSprout({ size: 22 });

      seedCard.innerHTML = `
        <span class="seed-icon">${speciesIcon}</span>
        <div style="flex:1;">
          <div class="seed-name">${species.name}</div>
          <div class="seed-desc">${species.description}</div>
        </div>
      `;

      seedCard.addEventListener('click', () => {
        this.plantManager?.selectSpecies(species);
        this.hideSeedTray();
        eventBus.emit('plant:startPlacing');
      });
      cardsContainer.appendChild(seedCard);
    }
    panel.appendChild(cardsContainer);

    const hint = document.createElement('div');
    hint.className = 'seed-tray-hint';
    hint.innerHTML = '选择种子后在土壤上点击种植<br>按 P 或 Esc 取消';
    panel.appendChild(hint);

    this.uiLayer.appendChild(panel);
  }

  toggleSeedTray(): void {
    this.isSeedTrayOpen = !this.isSeedTrayOpen;
    const panel = document.getElementById('seed-tray-panel');
    if (panel) {
      panel.style.display = this.isSeedTrayOpen ? 'flex' : 'none';
    }
    if (this.isSettingsOpen && this.isSeedTrayOpen) {
      this.isSettingsOpen = false;
      const settingsPanel = document.getElementById('settings-panel');
      if (settingsPanel) settingsPanel.style.display = 'none';
    }
  }

  hideSeedTray(): void {
    this.isSeedTrayOpen = false;
    const panel = document.getElementById('seed-tray-panel');
    if (panel) panel.style.display = 'none';
  }

  private createSettingsPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'settings-panel';
    panel.className = 'journal-panel settings-panel';

    // Settings content
    const settingsContent = document.createElement('div');
    settingsContent.id = 'settings-content';
    settingsContent.style.cssText = 'display:flex;flex-direction:column;gap:18px;';

    const title = document.createElement('h3');
    title.className = 'panel-title';
    title.textContent = '花园设置';
    settingsContent.appendChild(title);

    // Time speed
    const timeSection = document.createElement('div');
    timeSection.innerHTML = `
      <label class="settings-label">时间流速</label>
      <input type="range" id="time-speed" min="1" max="20" step="1" value="5" style="width:100%;">
      <div class="settings-slider-row">
        <span>慢</span>
        <span id="time-speed-value" class="slider-value">5x</span>
        <span>快</span>
      </div>
    `;
    settingsContent.appendChild(timeSection);

    // Actions
    const actions = document.createElement('div');
    actions.className = 'settings-actions';
    actions.innerHTML = `
      <button id="export-save" class="journal-btn"><span class="btn-icon">${iconDownload({size:16})}</span> 导出存档</button>
      <button id="import-save" class="journal-btn"><span class="btn-icon">${iconUpload({size:16})}</span> 导入存档</button>
      <input type="file" id="import-file" accept=".json" style="display:none;">
      <button id="garden-btn" class="journal-btn green"><span class="btn-icon">${iconFlower({size:16})}</span> 花园</button>
      <button id="debug-panel-toggle" class="journal-btn"><span class="btn-icon">${iconGear({size:16})}</span> 调试面板</button>
    `;
    settingsContent.appendChild(actions);
    panel.appendChild(settingsContent);

    // Garden content
    const gardenContent = document.createElement('div');
    gardenContent.id = 'garden-content';
    gardenContent.style.cssText = 'display:none;flex-direction:column;gap:14px;';

    const gardenHeader = document.createElement('div');
    gardenHeader.className = 'garden-header';
    gardenHeader.innerHTML = `
      <button id="garden-back-btn" class="garden-back-btn">← 返回</button>
      <h3 class="panel-title" style="border-bottom:none;padding-bottom:0;">花园</h3>
    `;
    gardenContent.appendChild(gardenHeader);

    const gardenList = document.createElement('div');
    gardenList.id = 'garden-list';
    gardenList.className = 'garden-list';
    gardenContent.appendChild(gardenList);

    const gardenCreateBtn = document.createElement('button');
    gardenCreateBtn.id = 'garden-create-btn';
    gardenCreateBtn.className = 'garden-create-btn';
    gardenCreateBtn.textContent = '＋ 新建花园';
    gardenContent.appendChild(gardenCreateBtn);

    const gardenCreateForm = document.createElement('div');
    gardenCreateForm.id = 'garden-create-form';
    gardenCreateForm.className = 'garden-create-form';
    gardenCreateForm.innerHTML = `
      <input id="garden-name-input" type="text" placeholder="输入花园名称..." maxlength="20" class="name-input">
      <div class="form-btns">
        <button id="garden-create-cancel" class="journal-btn">取消</button>
        <button id="garden-create-confirm" class="journal-btn confirm-btn">确认</button>
      </div>
    `;
    gardenContent.appendChild(gardenCreateForm);
    panel.appendChild(gardenContent);

    this.uiLayer.appendChild(panel);

    // Event listeners
    const timeSpeedSlider = panel.querySelector('#time-speed') as HTMLInputElement;
    const timeSpeedValue = panel.querySelector('#time-speed-value') as HTMLElement;
    timeSpeedSlider.addEventListener('input', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value);
      timeSpeedValue.textContent = value + 'x';
      eventBus.emit('settings:timeSpeed', value);
    });

    const exportBtn = panel.querySelector('#export-save') as HTMLButtonElement;
    exportBtn.addEventListener('click', () => eventBus.emit('game:export'));

    const importBtn = panel.querySelector('#import-save') as HTMLButtonElement;
    const importFile = panel.querySelector('#import-file') as HTMLInputElement;
    importBtn.addEventListener('click', () => importFile.click());
    importFile.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) eventBus.emit('game:import', file);
    });

    const gardenBtn = panel.querySelector('#garden-btn') as HTMLButtonElement;
    gardenBtn.addEventListener('click', () => this.showGardenView());

    const gardenBackBtn = panel.querySelector('#garden-back-btn') as HTMLButtonElement;
    gardenBackBtn.addEventListener('click', () => this.showSettingsView());

    const gardenCreateBtnEl = panel.querySelector('#garden-create-btn') as HTMLButtonElement;
    const gardenCreateFormEl = panel.querySelector('#garden-create-form') as HTMLElement;
    const gardenNameInput = panel.querySelector('#garden-name-input') as HTMLInputElement;
    const gardenCreateCancel = panel.querySelector('#garden-create-cancel') as HTMLButtonElement;
    const gardenCreateConfirm = panel.querySelector('#garden-create-confirm') as HTMLButtonElement;

    gardenCreateBtnEl.addEventListener('click', () => {
      gardenCreateBtnEl.style.display = 'none';
      gardenCreateFormEl.style.display = 'flex';
      gardenNameInput.focus();
    });

    const hideCreateForm = () => {
      gardenCreateFormEl.style.display = 'none';
      gardenCreateBtnEl.style.display = 'block';
      gardenNameInput.value = '';
    };

    gardenCreateCancel.addEventListener('click', hideCreateForm);

    const doCreate = () => {
      const name = gardenNameInput.value.trim() || '我的花园';
      hideCreateForm();
      eventBus.emit('garden:create', name);
    };
    gardenCreateConfirm.addEventListener('click', doCreate);
    gardenNameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doCreate();
    });

    const debugToggleBtn = panel.querySelector('#debug-panel-toggle') as HTMLButtonElement;
    debugToggleBtn.addEventListener('click', () => eventBus.emit('debug:toggle'));
  }

  private showSettingsView(): void {
    this.isGardenView = false;
    this.gardenDeletePending = null;
    const settingsContent = document.querySelector('#settings-content') as HTMLElement;
    const gardenContent = document.querySelector('#garden-content') as HTMLElement;
    if (settingsContent) settingsContent.style.display = 'flex';
    if (gardenContent) gardenContent.style.display = 'none';
  }

  showGardenView(): void {
    this.isGardenView = true;
    const settingsContent = document.querySelector('#settings-content') as HTMLElement;
    const gardenContent = document.querySelector('#garden-content') as HTMLElement;
    if (settingsContent) settingsContent.style.display = 'none';
    if (gardenContent) gardenContent.style.display = 'flex';
    this.renderGardenList();
  }

  private renderGardenList(): void {
    const list = document.querySelector('#garden-list') as HTMLElement;
    if (!list) return;

    const gardens = SaveManager.loadAllGardens();
    const currentId = SaveManager.getCurrentId();

    if (gardens.length === 0) {
      list.innerHTML = `<div class="garden-empty">还没有花园</div>`;
      return;
    }

    const formatDate = (ts: number): string => {
      const d = new Date(ts);
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const plantCount = (g: any) => g.saveData.plants ? g.saveData.plants.length : 1;

    list.innerHTML = gardens.map(g => {
      const isCurrent = g.id === currentId;

      let actionsHtml = '';
      if (isCurrent) {
        actionsHtml = `<span class="current-badge">当前</span>`;
      } else {
        const isDeletePending = this.gardenDeletePending === g.id;
        actionsHtml = `
          <div class="garden-actions">
            <button class="garden-switch-btn journal-btn" data-id="${g.id}">切换</button>
            <button class="garden-delete-btn journal-btn delete-btn ${isDeletePending ? 'pending' : ''}" data-id="${g.id}">${isDeletePending ? '确认删除' : '删除'}</button>
          </div>`;
      }

      return `
        <div class="garden-item ${isCurrent ? 'current' : ''}">
          <div style="display:flex;align-items:center;justify-content:space-between;">
            <span class="garden-name">${iconFlower({size:14})} ${this.escapeHtml(g.name)}</span>
            ${isCurrent ? actionsHtml : ''}
          </div>
          <div class="garden-meta">
            植物数量: ${plantCount(g)} · 创建于 ${formatDate(g.createdAt)}
          </div>
          <div class="garden-meta-secondary">
            上次游玩 ${formatDate(g.lastPlayedAt)}
          </div>
          ${!isCurrent ? actionsHtml : ''}
        </div>`;
    }).join('');

    list.querySelectorAll('.garden-switch-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.target as HTMLElement).getAttribute('data-id')!;
        eventBus.emit('garden:switch', id);
      });
    });

    list.querySelectorAll('.garden-delete-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.target as HTMLElement).getAttribute('data-id')!;
        if (this.gardenDeletePending === id) {
          this.gardenDeletePending = null;
          eventBus.emit('garden:delete', id);
        } else {
          this.gardenDeletePending = id;
          this.renderGardenList();
        }
      });
    });

    const createBtn = document.querySelector('#garden-create-btn') as HTMLElement;
    const createForm = document.querySelector('#garden-create-form') as HTMLElement;
    const nameInput = document.querySelector('#garden-name-input') as HTMLInputElement;
    if (createBtn && createForm) {
      createBtn.style.display = 'block';
      createForm.style.display = 'none';
      if (nameInput) nameInput.value = '';
    }
  }

  refreshGardenList(): void {
    if (this.isGardenView) {
      this.renderGardenList();
    }
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private toggleSettings(): void {
    this.isSettingsOpen = !this.isSettingsOpen;
    const panel = document.getElementById('settings-panel');
    if (panel) {
      panel.style.display = this.isSettingsOpen ? 'flex' : 'none';
    }
    if (this.isSeedTrayOpen && this.isSettingsOpen) {
      this.isSeedTrayOpen = false;
      const seedPanel = document.getElementById('seed-tray-panel');
      if (seedPanel) seedPanel.style.display = 'none';
    }
    if (!this.isSettingsOpen) {
      this.isGardenView = false;
      this.showSettingsView();
    }
  }

  private updateDisplay(): void {
    if (this.timeEl) this.timeEl.textContent = this.game.getFormattedTime();

    if (this.countEl) {
      const count = this.plants().length;
      if (count > 0) {
        this.countEl.textContent = `· ${count}株植物`;
      } else {
        this.countEl.textContent = '';
      }
    }
  }

  updateStageName(_name: string): void {
  }

  private updateCurrencyDisplay(): void {
    const el = document.getElementById('currency-display');
    if (el) {
      el.textContent = `💰 ${Math.floor(this.currencyManager.balance)}`;
    }
  }

  showNotification(text: string, duration: number = 2000): void {
    const notif = document.createElement('div');
    notif.className = 'notification';
    notif.innerHTML = text;
    notif.style.animationDuration = `${duration}ms`;
    document.body.appendChild(notif);

    setTimeout(() => {
      document.body.removeChild(notif);
    }, duration);
  }

  onCanvasMouseMove(x: number, y: number): void {
    this.updatePlantHover(x, y);
  }

  private updatePlantHover(cssX: number, cssY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const cx = cssX * (this.canvas.width / rect.width);
    const cy = cssY * (this.canvas.height / rect.height);

    let nearest: Plant | null = null;
    let nearestDist = Infinity;

    const allPlants = this.plants();
    for (const plant of allPlants) {
      const px = plant.position.x * this.canvas.width;
      const py = plant.position.y * this.canvas.height;
      const dist = Math.sqrt((cx - px) ** 2 + (cy - (py - 30)) ** 2);
      if (dist < 80 && dist < nearestDist) {
        nearest = plant;
        nearestDist = dist;
      }
    }

    if (nearest !== this.hoveredPlant) {
      this.hidePlantTags();
      if (nearest) {
        this.hoveredPlant = nearest;
        this.showPlantTags(nearest);
      } else {
        this.hoveredPlant = null;
      }
    }
  }

  private showPlantTags(plant: Plant): void {
    if (this.plantTagsContainer) return;

    this.controlTagExpanded = false;

    const stateNames: Record<string, string> = {
      thriving: '蓬勃', lazy: '慵懒', withering: '枯萎', dormant: '休眠'
    };
    const stateColors: Record<string, string> = {
      thriving: '#4a6b3a', lazy: '#b89830', withering: '#b06038', dormant: '#7a6a5a'
    };
    const stage = plant.getCurrentStage();
    const stageName = stage ? stage.name : '种子';
    const stateColor = stateColors[plant.state] || '#4a6b3a';
    const stateLabel = stateNames[plant.state] || plant.state;

    const container = document.createElement('div');
    container.className = 'plant-tags-container';

    const rect = this.canvas.getBoundingClientRect();
    const px = plant.position.x * this.canvas.width;
    const py = plant.position.y * this.canvas.height;
    const tagX = rect.left + px * (rect.width / this.canvas.width) - 70;
    const tagY = rect.top + (py - 60) * (rect.height / this.canvas.height);
    container.style.left = `${tagX}px`;
    container.style.top = `${tagY}px`;

    const healthTag = document.createElement('div');
    healthTag.className = 'plant-health-tag';
    healthTag.innerHTML = `
      <span class="tag-stage">${stageName}</span>
      <span class="tag-dot" style="background:${stateColor};"></span>
      <span class="tag-state">${stateLabel}</span>
      ${plant.currentStageIndex === plant.species.stages.length - 1 && plant.species.bloomingIncomeRate > 0 ? `<span class="tag-income">💰</span>` : ''}
    `;
    healthTag.addEventListener('click', (e) => {
      e.stopPropagation();
      eventBus.emit('plant:openInfo', plant);
    });
    container.appendChild(healthTag);

    const controlTag = document.createElement('div');
    controlTag.className = 'plant-control-tag';
    controlTag.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b5d4f" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>`;

    controlTag.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.controlTagExpanded) {
        this.collapseControlTag(container, plant);
      } else {
        this.expandControlTag(container, plant);
      }
    });
    container.appendChild(controlTag);

    container.addEventListener('mouseenter', () => {
      if (this.hideTagsTimeout) {
        clearTimeout(this.hideTagsTimeout);
        this.hideTagsTimeout = null;
      }
    });
    container.addEventListener('mouseleave', () => {
      this.scheduleHideTags();
    });

    this.uiLayer.appendChild(container);
    this.plantTagsContainer = container;
  }

  private expandControlTag(container: HTMLDivElement, plant: Plant): void {
    const controlTag = container.querySelector('.plant-control-tag') as HTMLDivElement;
    if (!controlTag) return;
    controlTag.remove();

    const lightOn = plant.supplementalLight > 50;

    const btnRow = document.createElement('div');
    btnRow.className = 'plant-action-row';

    const waterBtn = this.createPillButton(iconWaterDrop({size: 14}), () => {
      if (!this.currencyManager.spend(CurrencyManager.WATER_COST)) {
        this.showNotification('金币不足，无法浇水', 1500);
        return;
      }
      plant.water(plant.wateringAmount);
      this.showWaterEffect(plant);
    });

    const lightBtn = this.createPillButton(iconSun({size: 14}), () => {
      plant.toggleSupplementalLight();
      this.showLightEffect(plant);
      this.plantTagsContainer?.remove();
      this.plantTagsContainer = null;
      this.controlTagExpanded = false;
      this.showPlantTags(plant);
    });
    if (lightOn) {
      lightBtn.classList.add('light-on');
    }

    const settingsBtn = this.createPillButton(
      `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>`,
      () => {
        this.showPlantSettings(plant);
      }
    );

    btnRow.appendChild(waterBtn);
    btnRow.appendChild(lightBtn);
    btnRow.appendChild(settingsBtn);

    container.appendChild(btnRow);
    this.controlTagExpanded = true;
  }

  private collapseControlTag(container: HTMLDivElement, plant: Plant): void {
    const actionRow = container.querySelector('.plant-action-row');
    if (actionRow) actionRow.remove();

    const controlTag = document.createElement('div');
    controlTag.className = 'plant-control-tag';
    controlTag.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b5d4f" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>`;

    controlTag.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.controlTagExpanded) {
        this.collapseControlTag(container, plant);
      } else {
        this.expandControlTag(container, plant);
      }
    });
    container.appendChild(controlTag);
    this.controlTagExpanded = false;
  }

  private createPillButton(iconHtml: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'pill-btn';
    btn.innerHTML = iconHtml;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }

  private showWaterEffect(plant: Plant): void {
    const rect = this.canvas.getBoundingClientRect();
    const px = plant.position.x * this.canvas.width;
    const py = plant.position.y * this.canvas.height;
    const screenX = rect.left + px * (rect.width / this.canvas.width);
    const screenY = rect.top + (py - 20) * (rect.height / this.canvas.height);

    const effect = document.createElement('div');
    effect.className = 'effect-water';
    effect.style.left = `${screenX}px`;
    effect.style.top = `${screenY}px`;
    document.body.appendChild(effect);
    setTimeout(() => document.body.removeChild(effect), 800);
  }

  private showLightEffect(plant: Plant): void {
    const rect = this.canvas.getBoundingClientRect();
    const px = plant.position.x * this.canvas.width;
    const py = plant.position.y * this.canvas.height;
    const screenX = rect.left + px * (rect.width / this.canvas.width);
    const screenY = rect.top + (py - 40) * (rect.height / this.canvas.height);

    const effect = document.createElement('div');
    effect.className = plant.supplementalLight > 50 ? 'effect-light-on' : 'effect-light-off';
    effect.style.left = `${screenX}px`;
    effect.style.top = `${screenY}px`;
    document.body.appendChild(effect);
    setTimeout(() => document.body.removeChild(effect), 600);
  }

  private scheduleHideTags(): void {
    this.hideTagsTimeout = setTimeout(() => {
      this.hidePlantTags();
    }, 400);
  }

  hidePlantTags(): void {
    if (this.hideTagsTimeout) {
      clearTimeout(this.hideTagsTimeout);
      this.hideTagsTimeout = null;
    }
    if (this.plantTagsContainer) {
      this.plantTagsContainer.remove();
      this.plantTagsContainer = null;
    }
    this.controlTagExpanded = false;
    this.hoveredPlant = null;
  }

  showPlantSettings(plant: Plant): void {
    this.hidePlantTags();
    if (this.plantSettingsPanel) {
      this.plantSettingsPanel.remove();
    }

    const panel = document.createElement('div');
    panel.className = 'plant-settings-panel';

    panel.innerHTML = `
      <div class="ps-header">
        <span class="ps-title">
          ${iconFlower({size:16})} ${plant.species.name}
        </span>
        <button id="plant-settings-close" class="ps-close">&times;</button>
      </div>
      <div class="ps-slider-group">
        <div class="ps-slider-item">
          <label>单次浇水量</label>
          <input type="range" id="ps-moisture" min="1" max="50" step="1" value="${Math.round(plant.wateringAmount)}" style="width:100%;">
          <div class="slider-row">
            <span>1</span>
            <span id="ps-moisture-val" class="slider-value">${Math.round(plant.wateringAmount)}</span>
            <span>50</span>
          </div>
        </div>
        <div class="ps-slider-item">
          <label>补光强度</label>
          <input type="range" id="ps-light" min="0" max="100" step="1" value="${Math.round(plant.supplementalLight)}" style="width:100%;">
          <div class="slider-row">
            <span>0%</span>
            <span id="ps-light-val" class="slider-value">${Math.round(plant.supplementalLight)}%</span>
            <span>100%</span>
          </div>
        </div>
      </div>
      ${plant.currentStageIndex === plant.species.stages.length - 1 && plant.species.bloomingIncomeRate > 0 ? `
      <div class="ps-income">
        <span>💰</span>
        <span>盛花收益 +${(plant.species.bloomingIncomeRate * (plant.health / 100)).toFixed(3)}/分钟</span>
      </div>
      ` : ''}
      <div class="ps-footer">
        <button id="ps-delete" class="ps-delete-btn">铲除  +<span id="ps-sell-price"></span>💰</button>
      </div>
    `;

    this.uiLayer.appendChild(panel);
    this.plantSettingsPanel = panel;

    const moistSlider = panel.querySelector('#ps-moisture') as HTMLInputElement;
    const moistVal = panel.querySelector('#ps-moisture-val') as HTMLElement;
    moistSlider.addEventListener('input', () => {
      const v = parseInt(moistSlider.value);
      moistVal.textContent = String(v);
      plant.wateringAmount = v;
    });

    const lightSlider = panel.querySelector('#ps-light') as HTMLInputElement;
    const lightVal = panel.querySelector('#ps-light-val') as HTMLElement;
    lightSlider.addEventListener('input', () => {
      const v = parseInt(lightSlider.value);
      lightVal.textContent = v + '%';
      plant.setSupplementalLight(v);
    });

    const closeBtn = panel.querySelector('#plant-settings-close') as HTMLElement;
    closeBtn.addEventListener('click', () => this.hidePlantSettings());

    const sellPrice = this.currencyManager.getPlantSellPrice(plant);
    const sellPriceEl = panel.querySelector('#ps-sell-price') as HTMLElement;
    sellPriceEl.textContent = String(sellPrice);

    const deleteBtn = panel.querySelector('#ps-delete') as HTMLElement;
    deleteBtn.addEventListener('click', () => {
      eventBus.emit('plant:remove', plant, sellPrice);
      this.hidePlantSettings();
    });

    panel.addEventListener('click', (e) => e.stopPropagation());
  }

  hidePlantSettings(): void {
    if (this.plantSettingsPanel) {
      this.plantSettingsPanel.remove();
      this.plantSettingsPanel = null;
    }
  }

  isPlantPanelOpen(): boolean {
    return this.plantSettingsPanel !== null;
  }

  isHoverPopupOpen(): boolean {
    return this.plantTagsContainer !== null;
  }
}
